/**
 * @file shop-settings.ts
 *
 * The shop-settings surface for the admin Settings tab, over the real versioned API:
 * `GET /v1/shops/settings` (read the `general` block) and `PATCH /v1/shops/settings/general`
 * (partial update). The screens speak the flat, WooCommerce-flavoured {@link ShopSettings}
 * shape; {@link toShopSettings} / {@link toGeneralPatch} map that to and from the grouped
 * `/v1` contract at this boundary — including the single `email` field ↔ the backend's
 * `receiverEmail`, the shopfront `locale`, the date-format enum ↔ PHP-string, and the
 * number-format preset-key ↔ pipe-string (both clean bijections over the form's options).
 */

import { v1Get, v1Mutate } from './v1-request';
import type { ShopSettings } from './shop-types';
import type {
  CurrencyPositionV1,
  DateFormatV1,
  ShopSettingsResponseV1,
  UpdateGeneralSettingsRequestV1,
  WeightUnitV1,
} from './v1-types';

/** Backend date-format enum → the form's PHP date-format option value. */
const DATE_ENUM_TO_PHP: Record<DateFormatV1, string> = {
  iso: 'Y-m-d',
  us: 'm/d/Y',
  long: 'F j, Y',
  weekday: 'D, M j Y',
};

/**
 * The form's PHP date-format option value → backend enum. Derived as the exact inverse
 * of {@link DATE_ENUM_TO_PHP} so the two can't drift: adding a date format in one place
 * can't silently leave a value that reads fine but is dropped on write.
 */
const DATE_PHP_TO_ENUM: Record<string, DateFormatV1> = Object.fromEntries(
  (Object.entries(DATE_ENUM_TO_PHP) as [DateFormatV1, string][]).map(([enumValue, php]) => [
    php,
    enumValue,
  ]),
);

/**
 * The form's `thousandSep|decimalSep|numDecimals` pipe value → backend number-format
 * preset key. The `/v1` write accepts only a preset key (not raw separators), and the
 * form's seven options are exactly the seven presets, so this is a total bijection.
 */
const NUMBER_PIPE_TO_PRESET: Record<string, string> = {
  ',|.|2': 'comma_dot_2',
  '.|,|2': 'dot_comma_2',
  ' |,|2': 'space_comma_2',
  "'|.|2": 'apostrophe_dot_2',
  ',|.|0': 'comma_dot_0',
  '.|,|0': 'dot_comma_0',
  '|.|2': 'none_dot_2',
};

/**
 * Maps the `/v1` `general` block to the flat {@link ShopSettings} the screens consume:
 * nulls become empty strings, the single `email` field reads the backend `receiverEmail`,
 * the date enum resolves to its PHP-string option, and the number format is rebuilt as the
 * `thousandSep|decimalSep|numDecimals` pipe string from the resolved values.
 *
 * @param general - The `general` block of a `/v1` shop-settings response.
 *
 * @returns The flat shop settings.
 */
export function toShopSettings(general: ShopSettingsResponseV1['general']): ShopSettings {
  const nf = general.formatting.numberFormat;
  return {
    title: general.storeName ?? '',
    email: general.email?.receiverEmail ?? '',
    language: general.email?.language ?? '',
    date_format: DATE_ENUM_TO_PHP[general.formatting.dateFormat] ?? '',
    street_address: general.address?.street ?? '',
    city: general.address?.city ?? '',
    postcode: general.address?.zip ?? '',
    country: general.address?.country ?? '',
    currency: general.currency,
    currency_position: general.formatting.currencyPosition,
    number_format: `${nf.thousandSep}|${nf.decimalSep}|${nf.numDecimals}`,
    weight_unit: general.units.weight,
    locale: general.locale ?? '',
    timezone: general.timezone ?? 'UTC',
  };
}

/**
 * Maps a flat {@link ShopSettings} patch to the grouped `/v1` general update. Only provided
 * keys are sent; a value that would fail the frozen contract's validation is omitted or
 * cleared: an empty `currency`/`currency_position`/`weight_unit`/`date_format`/`number_format`
 * is left untouched, and an empty `country`/`email`/`language`/`locale` is sent as `null` to
 * clear it. The single `email` field maps to the backend's `receiverEmail`.
 *
 * @param patch - The flat shop-settings patch from the form.
 *
 * @returns The `PATCH /v1/shops/settings/general` request body.
 */
export function toGeneralPatch(patch: Partial<ShopSettings>): UpdateGeneralSettingsRequestV1 {
  const body: UpdateGeneralSettingsRequestV1 = {};

  if ('title' in patch) body.storeName = patch.title ? patch.title : null;
  if ('currency' in patch && patch.currency) body.currency = patch.currency;
  if ('locale' in patch) body.locale = patch.locale ? patch.locale : null;
  if ('timezone' in patch) body.timezone = patch.timezone ? patch.timezone : null;

  if ('email' in patch || 'language' in patch) {
    body.email = {};
    if ('email' in patch) body.email.receiverEmail = patch.email ? patch.email : null;
    if ('language' in patch) body.email.language = patch.language ? patch.language : null;
  }

  if ('street_address' in patch || 'city' in patch || 'postcode' in patch || 'country' in patch) {
    const address: NonNullable<UpdateGeneralSettingsRequestV1['address']> = {
      street: patch.street_address ?? '',
      city: patch.city ?? '',
      zip: patch.postcode ?? '',
      // Send `null` (not omit) for an empty country so clearing it actually clears the
      // stored value: the backend only touches the country column when the key is present.
      // A non-empty value is a 2-letter ISO code from the country <select>.
      country: patch.country ? patch.country : null,
    };
    body.address = address;
  }

  const formatting: NonNullable<UpdateGeneralSettingsRequestV1['formatting']> = {};
  if (patch.currency_position) {
    formatting.currencyPosition = patch.currency_position as CurrencyPositionV1;
  }
  if (patch.date_format && DATE_PHP_TO_ENUM[patch.date_format]) {
    formatting.dateFormat = DATE_PHP_TO_ENUM[patch.date_format];
  }
  if (patch.number_format && NUMBER_PIPE_TO_PRESET[patch.number_format]) {
    formatting.numberFormat = NUMBER_PIPE_TO_PRESET[patch.number_format];
  }
  if (Object.keys(formatting).length > 0) body.formatting = formatting;

  if (patch.weight_unit) body.units = { weight: patch.weight_unit as WeightUnitV1 };

  return body;
}

/**
 * Fetches the shop settings (the Settings-tab business fields, incl. the shopfront locale).
 *
 * @returns The flat shop settings.
 *
 * @throws {WcApiError} When the request fails.
 */
export async function fetchShopSettings(): Promise<ShopSettings> {
  const res = await v1Get<ShopSettingsResponseV1>('/shops/settings');
  return toShopSettings(res.data.general);
}

/**
 * Updates the shop settings with a partial patch (`PATCH /v1/shops/settings/general`).
 *
 * @param settings - The fields to change (flat shape; e.g. `{ title, email, locale }`).
 *
 * @returns The full saved settings.
 *
 * @throws {WcApiError} When the request fails.
 */
export async function updateShopSettings(
  settings: Partial<ShopSettings>,
): Promise<ShopSettings> {
  const res = await v1Mutate<ShopSettingsResponseV1>(
    '/shops/settings/general',
    'PATCH',
    toGeneralPatch(settings),
  );
  return toShopSettings(res.general);
}
